import { Grid, Typography } from '@mui/material';
import { AxiosResponse } from 'axios';
import { BodyExceptionListVM } from 'practice/view-models/practice-detail';
import { memo, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { PracticeData } from 'shell/models/practice';
import TableException from './table';

export enum CodiciEccezioniIgnorabili {
  TernaPresentiIndirizzi = '9026',
}

// eccezioni sui documenti mancanti: non ignorabili e non bypassabili
const CODICI_ERRORI_DOCUMENTI = ['9000', '9100'];

export const isErroreDocumento = (item: BodyExceptionListVM): boolean =>
  CODICI_ERRORI_DOCUMENTI.includes(item.codMessaggio?.toString() || '');

interface ExceptionTablesProps {
  // eccezioni ignorabili: comprendono messaggi catasto ed errori documenti
  eccezioniIgnorabili?: Array<BodyExceptionListVM>;
  eccezioniAnpr?: Array<BodyExceptionListVM>;
}

/**
 * Tabelle delle eccezioni condivise tra acquisizione e gestione pratica:
 * "Dettaglio messaggi Catasto", "Errori documenti" ed "Eccezioni ANPR"
 */
const ExceptionTablesComponent = ({
  eccezioniIgnorabili,
  eccezioniAnpr,
}: ExceptionTablesProps): React.ReactElement => {
  const params = useParams();

  // Indirizzi associati alla terna (tabella indirizzo_catasto della pratica),
  // da riportare indentati a dx sotto all'eccezione CodiciEccezioniIgnorabili.TernaPresentiIndirizzi.
  const [cadastralAddresses] = useHttpFetch<HttpResponse<PracticeData>, Array<string>>(
    `${__PRACTICE_API_URL__}/pratiche/pratica/${params.codiceEnte}-${params.idAnnoPratica}-${params.tipoRegistro}-${params.progressivoPratica}`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
        data: {},
      },
      transformer: (response: AxiosResponse<HttpResponse<PracticeData>>) =>
        (response?.data?.payload?.indirizzoCatastoVOList || [])
          .map((item) => item.descIndirizzoCatasto || '')
          .filter((address) => Boolean(address)),
    }
  );

  const erroriDocumenti = useMemo(
    () => (eccezioniIgnorabili || []).filter(isErroreDocumento),
    [eccezioniIgnorabili]
  );

  const messaggiCatasto = useMemo(() => {
    const list = (eccezioniIgnorabili || []).filter((item) => !isErroreDocumento(item));

    const addresses = (cadastralAddresses?.data as Array<string>) || [];

    if (!addresses.length) {
      return list;
    }

    const indexEccezioneCatasto = list.findIndex(
      (item) => item.codMessaggio?.toString() === CodiciEccezioniIgnorabili.TernaPresentiIndirizzi
    );

    if (indexEccezioneCatasto === -1) {
      return list;
    }

    const addressRows: Array<BodyExceptionListVM> = addresses.map((address, index) => ({
      exceptionDetail: address,
      isCadastralAddress: true,
      idEccezione: `${CodiciEccezioniIgnorabili.TernaPresentiIndirizzi}-indirizzo-${index}`,
    }));

    return [
      ...list.slice(0, indexEccezioneCatasto + 1),
      ...addressRows,
      ...list.slice(indexEccezioneCatasto + 1),
    ];
  }, [eccezioniIgnorabili, cadastralAddresses?.data]);

  if (!messaggiCatasto.length && !erroriDocumenti.length && !eccezioniAnpr?.length) {
    return <Typography>Nessuna informazione</Typography>;
  }

  return (
    <Grid container spacing={1} item xs={12}>
      {messaggiCatasto.length > 0 ? (
        <Grid item xs={12}>
          <Typography>Dettaglio messaggi Catasto</Typography>
          <TableException data={messaggiCatasto} hasIcons />
        </Grid>
      ) : undefined}
      {erroriDocumenti.length > 0 ? (
        <Grid item xs={12}>
          <Typography>Errori Documenti</Typography>
          <TableException data={erroriDocumenti} hasIcons />
        </Grid>
      ) : undefined}
      {eccezioniAnpr && eccezioniAnpr.length > 0 ? (
        <Grid item xs={12}>
          <Typography>Eccezioni ANPR</Typography>
          <TableException data={eccezioniAnpr} />
        </Grid>
      ) : undefined}
    </Grid>
  );
};

ExceptionTablesComponent.displayName = 'Practice - Exception tables';

const ExceptionTables = memo(ExceptionTablesComponent);

export default ExceptionTables;
